<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
        <BillingFeatureGate feature="returns"
                            variant="page"
                            :title="$t('supplierReturnsLabel')"
                            :description="$t('billing.features.returns.tooltip')"
                            icon="ti-back-left">
          <Toolbar class="p-mb-4">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('supplierReturnsLabel') }}</div>
            </template>
            <template #end>
              <div class="p-d-flex p-flex-wrap">
                <div class="p-d-flex p-ai-center p-mr-2" v-if="$store.getters.isAccountantOrHigher">
                  <Checkbox @change="changeRequestFilters"
                            :disabled="dataTableIsLoading"
                            id="returns-show-only-not-fully-invoiced-checkbox"
                            :binary="true"
                            v-model="showOnlyNotFullyInvoiced"/>
                  <div>
                    <label for="returns-show-only-not-fully-invoiced-checkbox">
                      <div class="pointer p-p-1">{{ $t('showOnlyNotFullyInvoicedLabel') }}</div>
                    </label>
                  </div>
                </div>
                <CustomDropdown id="supplier"
                                class="p-mr-2"
                                :disabled="dataTableIsLoading"
                                style="min-width: 180px; max-width: 200px;"
                                ref="selectedSupplierDropdown"
                                :searchData="supplierSearchData"
                                v-model="selectedSupplier"
                                data-key="id"
                                :options="suppliersData"
                                :filterPlaceholder="$t('lineItemSearchSupplierPlaceholder')"
                                optionLabel="company_name"
                                :filter="true"
                                :filterFields="['name', 'company_name', 'reg_number']"
                                :loading="suppliersDataIsLoading"
                                @change="changeSupplier"
                                @filter="searchSuppliers"
                                :showClear="true">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <div>{{ slotProps.value.company_name }} <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></div>
                    </div>
                    <span v-else>{{ $t('lineItemSelectSupplierPlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div v-if="slotProps.option">
                      <div>{{ slotProps.option.company_name }} <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></div>
                    </div>
                  </template>
                </CustomDropdown>
                <SearchInput :disabled="dataTableIsLoading"
                             @change-processed-search-value="changeProcessedSearchValue"
                             v-model="searchData"
                             @show-spinner="isLoading = true"/>
              </div>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :dataTableIsLoading="dataTableIsLoading"
                            :showHeader="false"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                                  {name: 'code', title: $t('Code'), sortable: false},
                                  {name: 'name', title: $t('Name'), sortable: false},
                                  {name: 'supplier', title: $t('Supplier'), sortable: false},
                                  {name: 'qty', title: $t('Qty'), minWidth: '70px', sortable: false},
                                  {name: 'purchase_price', title: $t('purchasePriceShortLabel'), minWidth: '70px', sortable: false},
                                  {name: 'sell_price', title: $t('sellingPriceShortLabel'), minWidth: '70px', sortable: false},
                                  {name: 'order', width: '40px', sortable: false},
                                  // {name: 'type', title: $t('Type'), sortable: false},
                                  {name: 'reason', title: $t('Reason'), sortable: false},
                                  // {name: '', title: '', sortable: false},
                                  {name: 'comment', sortable: false},
                                  // {name: 'fixed_qty', title: '', class: 'ti-link', sortable: false},
                                  {name: 'fixed_qty', title: null, sortable: false},
                                  {name: 'created_at', title: $t('Created'), sortable: false},
                              ]">
            <template v-slot:body>
              <tr v-for="(item, index) of items"
                  :key="index"
                  :class="{'updated-item': isItemUpdated(item.id)}">
                <td>
                  <span v-if="item.position_data?.code">{{ item.position_data.code }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.position_data?.name">{{ item.position_data.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.supplier">{{ item.supplier.company_name }}</span>
                </td>
                <td>{{ formatDecimal(item.qty) }}</td>
                <td>
                  <span v-if="item.position_data?.purchase_price">{{ formatCurrency(item.position_data.purchase_price, 2, 4) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.position_data?.sell_price">{{ formatCurrency(item.position_data.sell_price, 2, 4) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
<!--                  <span v-if="item.order?.uid" class="p-d-flex p-ai-center">-->
<!--                    <router-link :to="{ path: `/orders/${item.order.uid}` }" target="_blank" draggable="false">-->
<!--                      <Button class="p-button-text p-p-1" v-tooltip.top="item.order?.number ? item.order.number : null">-->
<!--                        <i class="ti-hummer"/>-->
<!--                      </Button>-->
<!--                    </router-link>-->
<!--                  </span>-->
<!--                  <i v-else class="ti-minus"/>-->
                  <OpenOrderButton :position="item"/>
                </td>
                <td>
  <!--                <span v-if="item.reason">{{ ReducedPositionReasons.find(reason => reason.id === item.reason)?.label[$i18n.locale] }}</span>-->
                  <span v-if="item.reason">{{ reducedReasonLabel(item.reason) }}</span>
                  <template v-else-if="item.purchaseInvoice">
                    <router-link v-if="item.purchaseInvoice.uid"
                                 :to="{ path: '/purchase-invoices', query: { show: item.purchaseInvoice.uid } }"
                                 :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                                 rel="noopener"
                                 draggable="false">
                      <span>{{ item.purchaseInvoice.number }}</span>
                    </router-link>
                    <i v-else class="ti-minus"/>
                  </template>
                </td>
                <td>
                  <span v-if="item.comment" v-tooltip.top="item.comment">
                    <i class="ti-comment-alt"/>
                  </span>
                </td>
                <td>
                  <PositionFixedQtyButton v-if="item.fixed_qty && +item.fixed_qty" :position="item" fixedMode="return"/>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="table-date-line">
                  {{ formatBy(item.created_at, item.creator) }}
                </td>
                <td>
                  <div class="actions">
                    <EditButton @click="editItem(item)"/>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="!itemsArrayIsEmpty"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <ReturnedPositionModal :visible="itemModal"
                                 :item="item"
                                 :taxes="taxes"
                                 @update-items="updateItems"
                                 @close="closeItemModal"/>
        </BillingFeatureGate>
			</div>
		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import formatMixins from "@/mixins/formatMixins";
import PositionFixedQtyButton from "@/components/PositionFixedQtyButton";
import ReducedPositionReasons from "@/translations/ReducedPositionReasons";
import ReturnedPositionModal from "@/pages/warehouse/components/ReturnedPositionModal.vue";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import OpenOrderButton from "@/components/OpenOrderButton.vue";

export default {
  mixins: [ httpMixins, datatableMixins, formatMixins, showErrorsMixins, billingFeaturesMixins ],
  components: {OpenOrderButton, PositionFixedQtyButton, ReturnedPositionModal, BillingFeatureGate },
	data() {
		return {
      showOnlyNotFullyInvoiced: true,
      suppliers: null,
      fieldsExpand: 'creator,order,position_data,supplier,purchaseInvoice',
      apiLink: 'reduced-position',
      routePath: '/supplier-returns',
      sort: {
        column: 'id',
        direction: '-',
      },
      ReducedPositionReasons,
		}
	},
  watch: {
    suppliers(value) {
      if (value?.length) {
        this.suppliersData = [...value]
      } else {
        this.suppliersData = null
      }
    },
    canUseReturns(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetReturnsState()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    // this.checkRouteQuery()
    // this.getPageData()
    if (this.canUseReturns) {
      this.checkRouteQuery()
      this.getPageData()
    }
	},
  methods: {
    getPageData() {
      if (!this.canUseReturns) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems()
      // this.getSuppliers().then(() => this.checkSupplierInArray())
      this.getSuppliers().then(() => {
        if (this.canUseReturns) {
          this.checkSupplierInArray()
        }
      })
      // this.getModalData().then(() => this.checkSupplierInArray())
    },
    reducedReasonLabel(id) {
      const reason = this.ReducedPositionReasons.find(reason => Number(reason.id) === Number(id))

      return reason?.label?.[this.$i18n.locale] || reason?.label?.en || ''
    },
    resetReturnsState() {
      this._abortors?.returns?.abort?.()

      this.items = []
      this.suppliers = null
      this.item = {}
      this.itemModal = false

      this.isLoading = false
      this.dataTableIsLoading = false

      this.$store.commit('toggleFirstLayer', false)
    },
    // changeReducedPositionsType() {
    //   if (this.selectedTypeId === this.selectedType?.id) return false
    //   this.selectedTypeId = this.selectedType?.id
    //
    //   const route = {
    //     route: this.$route.path,
    //     query: {
    //       ...this.$route.query,
    //       type: this.selectedTypeId,
    //     }
    //   }
    //
    //   if (!this.selectedTypeId && this.selectedTypeId !== 0) {
    //     delete route.query.type
    //   }
    //   this.$router.push(route)
    // },
    async getItems(showSpinner = false) {
      if (!this.canUseReturns) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      if (showSpinner) this.isLoading = true
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('returns')
      try {
        const { status, data, headers } = await httpClient(`${this.apiLink}`, {
          params: {
            supplierId: this.selectedSupplierId,
            page: this.pagination.currentPage,
            // typeId: this.selectedTypeId,
            typeId: 1,
            show_only_unfixed: +this.showOnlyNotFullyInvoiced,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand
          },
          signal: ctrl?.signal,
        })

        if (!this.canUseReturns) return

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.returns === ctrl) {
          this._abortors.returns = null
        }
      }
    },
	},
  computed: {
    actionButtonsCount() {
      return 1
    },
  }
}
</script>